/**
 * Change Business Address (Section 47(3) / PD 2/2017) — M32017
 * DCR + Form PD2 + Section 47(3) Notification. 필드 키는 Word 템플릿 [[변수]] 와 일치.
 */
export type ChangeBusinessAddressFieldType = 'text' | 'date' | 'entity' | 'person' | 'address' | 'clause' | 'msic';

export const CHANGE_BUSINESS_ADDRESS_KEYS = [
  'company_name',
  'company_registration_no',
  'constitution_clause_number',
  'old_business_address',
  'address_line_1',
  'address_line_2',
  'address_line_3',
  'postcode',
  'city',
  'state',
  'country',
  'effective_date',
  'dcr_date',
  'declaration_date',
  'nature_of_business',
  'nature_change_date',
] as const;

export type ChangeBusinessAddressFieldKey = (typeof CHANGE_BUSINESS_ADDRESS_KEYS)[number];

const LABELS: Record<string, string> = {
  company_name: 'Company Name',
  company_registration_no: 'Registration No.',
  constitution_clause_number: 'Clause / Article No.',
  old_business_address: 'Old Business Address',
  address_line_1: 'Address Line 1',
  address_line_2: 'Address Line 2',
  address_line_3: 'Address Line 3',
  postcode: 'Postcode',
  city: 'City',
  state: 'State',
  country: 'Country',
  effective_date: 'Effective Date',
  dcr_date: 'Resolution Date',
  declaration_date: 'Declaration Date',
  nature_of_business: 'Nature of Business',
  nature_change_date: 'Date of Change',
};

export function labelize(key: string): string {
  return LABELS[key] || key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * 신주소(변경 후) 구성 필드 — 이 필드들의 📍 챗봇 삽입 시 한 번에 분배 채움.
 *   address_line_1/2/3 + postcode + city + state + country
 */
export const NEW_ADDRESS_KEYS = [
  'address_line_1',
  'address_line_2',
  'address_line_3',
  'postcode',
  'city',
  'state',
  'country',
] as const;

export function typeOf(key: string): ChangeBusinessAddressFieldType {
  if (key === 'constitution_clause_number') return 'clause';
  if (/_date$/.test(key)) return 'date';
  // old_business_address(단일 합성) + 신주소 구성 필드 전부 → 📍 주소 챗봇 트리거
  if (key === 'old_business_address' || (NEW_ADDRESS_KEYS as readonly string[]).includes(key)) return 'address';
  if (key === 'company_name') return 'entity';
  if (key === 'nature_of_business') return 'msic';
  return 'text';
}

export function isOptional(key: string): boolean {
  return [
    'constitution_clause_number',
    'address_line_2',
    'address_line_3',
    'nature_of_business',
    'nature_change_date',
  ].includes(key);
}

/** required progress keys */
export const CHANGE_BUSINESS_ADDRESS_REQUIRED = CHANGE_BUSINESS_ADDRESS_KEYS.filter((k) => !isOptional(k));

/** DCR·Section47(3) 공통 records 체크리스트 (Word [[if record_a]] … [[if record_k]]). */
export const RECORD_ITEMS: { key: string; text: string }[] = [
  { key: 'record_a', text: 'Notice of registration issued under section 15 of the Companies Act 2016' },
  { key: 'record_b', text: 'The constitution of the company, if any' },
  { key: 'record_c', text: 'Certificates given under the Companies Act 2016 or corresponding previous written law, if any' },
  { key: 'record_d', text: 'All registers, books, records and document as required under the Companies Act 2016' },
  { key: 'record_e', text: 'Minutes of all meetings of members and resolutions of members' },
  { key: 'record_f', text: 'Minutes of all meetings and resolutions of the Board and committees of the Board' },
  { key: 'record_g', text: 'Copies of all written communications to all members or all holders of the same class of shares' },
  { key: 'record_h', text: 'Copies of all financial statements and group financial statement' },
  { key: 'record_i', text: 'The accounting records of the company required under section 245' },
  { key: 'record_j', text: 'Copies of all instruments creating or evidencing charges as required under section 357 of the Companies Act 2016' },
  { key: 'record_k', text: 'Such other documents required to be kept by the Registrar' },
];
